import { PrintTemplatesService } from '@/services/print-templates/print-templates.service';
import { isPrintableRecordId } from '@/shared/directives/printable-list.directive';
import { PrintTemplateDto } from '@/shared/openapi';
import { templateSubjectLabel } from '@/shared/utils/template-subject.util';
import { Component, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { PrintTemplateDialogService } from '../print-template-dialog.service';

/**
 * Prints the record of an expanded table row: directly when its subject has
 * one record template, from a small menu when it has several. Shows nothing
 * when it has none, or for rows that are not records (e.g. grouping rows).
 */
@Component({
  selector: 'app-row-print-button',
  standalone: true,
  imports: [MatIconModule, MatMenuModule, TranslocoPipe],
  templateUrl: './row-print-button.component.html',
  styleUrl: './row-print-button.component.scss',
  host: {
    '[class.row-print-shown]': 'templates().length > 0',
  },
})
export class RowPrintButtonComponent {
  readonly subject = input.required<string>();
  readonly recordId = input.required<unknown>();

  private readonly printTemplates = inject(PrintTemplatesService);
  private readonly printDialog = inject(PrintTemplateDialogService);
  private readonly transloco = inject(TranslocoService);

  private readonly assignments = toSignal(this.printTemplates.all(), {
    initialValue: [] as PrintTemplateDto[],
  });

  protected readonly templates = computed(() => {
    if (!isPrintableRecordId(this.recordId())) {
      return [];
    }
    return this.assignments().filter(
      assignment =>
        assignment.subject === this.subject() &&
        assignment.scope === PrintTemplateDto.ScopeEnum.Detail
    );
  });

  protected print(template: PrintTemplateDto): void {
    this.printDialog.open({
      assignment: template,
      ids: [this.recordId() as string],
      subjectLabel: templateSubjectLabel(this.transloco, this.subject()),
    });
  }
}
